import React, { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  User,
  CheckCircle,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Youtube,
} from "lucide-react";
import SecondaryNavbar from "../../components/common/SecondaryNavbar";
import SecondaryHero from "../../components/common/SecondaryHero";

const ContactPage = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Contact information
  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Phone",
      content: "+91 876 320 9815",
      description: "Mon to Fri 9am to 6pm",
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email",
      content: "mtspvtltd2012@gmail.com",
      description: "Send us your query anytime!",
      color: "from-green-500 to-emerald-500",
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Office",
      content:
        "N - 5/111, Ground Floor, Biju Pattnaik College Road,Jaydev Vihar, Bhubaneswar, Odisha",
      description: "",
      color: "from-purple-500 to-pink-500",
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Hours",
      content: "Monday - Friday",
      description: "9:00 AM - 6:00 PM",
      color: "from-orange-500 to-red-500",
    },
  ];

  // Services
  const services = [
    "GIS Services",
    "Software Development",
    "Consulting",
    "Printing & Scanning",
    "Drone Surveys",
    "Environmental Consulting",
    "Other",
  ];

  // Social links
  const socialLinks = [
    {
      icon: <Facebook className="w-5 h-5" />,
      name: "Facebook",
      href: "#",
      color: "hover:bg-blue-600",
    },
    {
      icon: <Twitter className="w-5 h-5" />,
      name: "Twitter",
      href: "#",
      color: "hover:bg-sky-500",
    },
    {
      icon: <Linkedin className="w-5 h-5" />,
      name: "LinkedIn",
      href: "#",
      color: "hover:bg-blue-700",
    },
    {
      icon: <Instagram className="w-5 h-5" />,
      name: "Instagram",
      href: "#",
      color: "hover:bg-pink-600",
    },
    {
      icon: <Youtube className="w-5 h-5" />,
      name: "YouTube",
      href: "#",
      color: "hover:bg-red-600",
    },
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setIsSubmitted(true);

    // Reset form after success
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: "",
        message: "",
      });
    }, 5000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.6,
      },
    },
  };

  return (
    <div className="overflow-hidden">
      <SecondaryNavbar />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <SecondaryHero
          pageName="Get In Touch"
          pageHead1="Let's"
          pageHead2=""
          pageHead3=""
          pageHead4="Connect"
          pageDesc="Ready to start your next project? We're here to help bring your vision to life."
          bgImgUrl="https://store-images.s-microsoft.com/image/apps.4083.14191174512742283.25b58903-5b4d-47c0-959c-c79a5aabf46c.17e473bf-b171-4bc5-b00e-f1a6f54977e1"
        />

        {/* Contact Content */}
        <section ref={sectionRef} className="py-6 lg:py-12">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Contact Information - 1 column */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={
                  isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }
                }
                transition={{ duration: 0.8 }}
                className="lg:col-span-1"
              >
                <h2 className="text-3xl lg:text-4xl font-bold text-primaryTextColor mb-4 font-lexend">
                  Get in Touch
                </h2>

                <p className="text-secondaryTextColor text-lg leading-relaxed mb-6">
                  Have a project in mind? Let's discuss how we can help you
                  achieve your goals with our expertise and innovative
                  solutions.
                </p>

                {/* Contact Cards */}
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  className="space-y-6 mb-12"
                >
                  {contactInfo.map((item, index) => (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      className="group p-6 bg-white rounded-2xl border border-gray-200 hover:border-brandBlue/30 transition-all duration-500 hover:shadow-xl"
                    >
                      <div className="flex items-start space-x-4">
                        <div
                          className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}
                        >
                          {item.icon}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-primaryTextColor mb-1">
                            {item.title}
                          </h3>
                          <p className="text-primaryTextColor font-medium mb-1">
                            {item.content}
                          </p>
                          <p className="text-secondaryTextColor text-sm">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Social Links */}
                <div>
                  <h3 className="text-xl font-semibold text-primaryTextColor mb-6 font-lexend">
                    Follow Us
                  </h3>
                  <div className="flex space-x-3">
                    {socialLinks.map((social, index) => (
                      <motion.a
                        key={index}
                        href={social.href}
                        className="w-12 h-12 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center text-secondaryTextColor transition-all duration-300 hover:text-white"
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {social.icon}
                      </motion.a>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Contact Form - 2 columns */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={
                  isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }
                }
                transition={{ duration: 0.8, delay: 0.2 }}
                className="lg:col-span-2"
              >
                <div className="bg-white rounded-3xl border border-gray-200 p-8 lg:p-12 shadow-sm">
                  {isSubmitted ? (
                    /* Success State */
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-12"
                    >
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-green-500" />
                      </div>
                      <h3 className="text-2xl font-bold text-primaryTextColor mb-4 font-lexend">
                        Message Sent Successfully!
                      </h3>
                      <p className="text-secondaryTextColor text-lg mb-8 max-w-md mx-auto">
                        Thank you for reaching out. We'll get back to you within
                        2 hours.
                      </p>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setIsSubmitted(false)}
                        className="px-8 py-3 bg-brandBlue text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
                      >
                        Send Another Message
                      </motion.button>
                    </motion.div>
                  ) : (
                    /* Contact Form */
                    <>
                      <h2 className="text-3xl lg:text-4xl font-bold text-primaryTextColor mb-2 font-lexend">
                        Send us a Message
                      </h2>
                      <p className="text-secondaryTextColor text-lg mb-8">
                        Fill out the form below and we'll get back to you as
                        soon as possible.
                      </p>

                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Name */}
                          <div>
                            <label className="block text-sm font-semibold text-primaryTextColor mb-3">
                              Full Name *
                            </label>
                            <div className="relative">
                              <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                              <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brandBlue focus:border-transparent transition-all duration-300"
                                placeholder="Enter your full name"
                              />
                            </div>
                          </div>

                          {/* Email */}
                          <div>
                            <label className="block text-sm font-semibold text-primaryTextColor mb-3">
                              Email Address *
                            </label>
                            <div className="relative">
                              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                              <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brandBlue focus:border-transparent transition-all duration-300"
                                placeholder="Enter your email"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Phone */}
                          <div>
                            <label className="block text-sm font-semibold text-primaryTextColor mb-3">
                              Phone Number
                            </label>
                            <div className="relative">
                              <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                              <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brandBlue focus:border-transparent transition-all duration-300"
                                placeholder="Enter your phone number"
                              />
                            </div>
                          </div>

                          {/* Company */}
                          <div>
                            <label className="block text-sm font-semibold text-primaryTextColor mb-3">
                              Company
                            </label>
                            <input
                              type="text"
                              name="company"
                              value={formData.company}
                              onChange={handleInputChange}
                              className="w-full px-4 py-4 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brandBlue focus:border-transparent transition-all duration-300"
                              placeholder="Enter company name"
                            />
                          </div>
                        </div>

                        {/* Service */}
                        <div>
                          <label className="block text-sm font-semibold text-primaryTextColor mb-3">
                            Service Interested In
                          </label>
                          <select
                            name="service"
                            value={formData.service}
                            onChange={handleInputChange}
                            className="w-full px-4 py-4 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brandBlue focus:border-transparent transition-all duration-300"
                          >
                            <option value="">Select a service</option>
                            {services.map((service, index) => (
                              <option key={index} value={service}>
                                {service}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Message */}
                        <div>
                          <label className="block text-sm font-semibold text-primaryTextColor mb-3">
                            Message *
                          </label>
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            required
                            rows={6}
                            className="w-full px-4 py-4 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brandBlue focus:border-transparent transition-all duration-300 resize-none"
                            placeholder="Tell us about your project requirements..."
                          />
                        </div>

                        {/* Submit Button */}
                        <motion.button
                          type="submit"
                          disabled={isSubmitting}
                          className={`w-full py-4 text-white font-semibold rounded-xl transition-all duration-300 flex items-center justify-center space-x-3 ${
                            isSubmitting
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-gradient-to-r from-brandBlue to-brandGreen hover:shadow-2xl"
                          }`}
                          whileHover={
                            !isSubmitting ? { scale: 1.02, y: -2 } : {}
                          }
                          whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                        >
                          {isSubmitting ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              <span>Sending...</span>
                            </>
                          ) : (
                            <>
                              <span>Send Message</span>
                              <Send className="w-5 h-5" />
                            </>
                          )}
                        </motion.button>
                      </form>
                    </>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-12 bg-white border-t border-gray-200">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-primaryTextColor mb-4 font-lexend">
                Visit Our Office
              </h2>
              <p className="text-secondaryTextColor text-lg max-w-2xl mx-auto">
                Come see us at our headquarters. We'd love to meet you in person
                and discuss your project.
              </p>
            </motion.div>

            {/* Map Embed */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="rounded-3xl overflow-hidden h-96 shadow-lg"
            >
              <iframe
                title="Our Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3742.064480908157!2d85.81283167547102!3d20.29759798117501!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a1909caccedca53%3A0xe9fdd57af808c713!2sMAPTECH%20SOLUTIONS%20PVT.LTD.!5e0!3m2!1sen!2sin!4v1764925761603!5m2!1sen!2sin"
                width="100%"
                height="100%"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full border-0"
              ></iframe>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContactPage;
